'use client';

import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_HOVER,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_POPOVER_W,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT,
  attributeLabelWidthStyle,
  attributePlaceholder
} from '@/components/attributes';
import { AttributeRow } from './attribute-row';
import { Button } from '@/components/ui/button';
import { InlineCreateStep } from './inline-create-step';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import {
  useStocks,
  type TickerSearchResult
} from '@/lib/services/stocks/hooks';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';

/**
 * Custom HIN share (not yet created in backend)
 * Used when user can't find existing ticker
 */
export interface CustomHinShare {
  ticker: string;
  companyName: string;
  isCustom: true;
}

/**
 * HIN Share value can be a TickerSearchResult, CustomHinShare, or string (legacy format)
 */
export type HinShareValue =
  | TickerSearchResult
  | CustomHinShare
  | string
  | null
  | undefined;

interface HinShareSearchFieldProps {
  /** Current HIN share value (object or string) */
  value?: HinShareValue;

  /** Callback when HIN share changes */
  onChange: (
    share: TickerSearchResult | CustomHinShare | string | null
  ) => void;

  /** Display mode */
  mode: 'read' | 'edit';

  /** Whether field is disabled */
  disabled?: boolean;

  /** Whether field is required */
  required?: boolean;

  /**
   * Which chrome to wear (LF-6314 follow-up). Defaults to `form`, so the shares
   * forms are unchanged.
   *
   * `compact` is the attribute treatment, exactly as `InstitutionSearchField`'s is:
   * the field becomes a row on the shared label column and the trigger wears the
   * value cell's ring and type instead of its own grey fill. **The popover, the
   * search, the create step and every behaviour are identical** — ink and geometry
   * only, which is why it is a variant rather than a second component.
   */
  variant?: 'form' | 'compact' | 'cell';
}

/**
 * Type guard to check if share is custom
 */
function _isCustomHinShare(
  share: TickerSearchResult | CustomHinShare | null
): share is CustomHinShare {
  return share !== null && 'isCustom' in share && share.isCustom === true;
}

/**
 * HIN share search field component
 *
 * Features:
 * - Read mode: displays ticker and company name
 * - Edit mode: searchable dropdown with backend fuzzy search
 * - Returns complete TickerSearchResult object
 * - Handles both object and string formats
 * - Supports custom ticker entry for stocks not found in search
 */
export function HinShareSearchField({
  value,
  onChange,
  mode,
  disabled = false,
  required = false,
  variant = 'form'
}: HinShareSearchFieldProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');

  // Debounce the search term to avoid too many API calls
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Use React hook for fetching stocks
  const { data, isLoading } = useStocks({
    searchQuery: debouncedSearchTerm
  });

  const stocks = data || [];

  // Parse current value to get stock
  const currentStock = useMemo(() => {
    if (!value) return null;

    // If it's already a TickerSearchResult or CustomHinShare object
    if (typeof value === 'object' && 'ticker' in value) {
      return value as TickerSearchResult | CustomHinShare;
    }

    // If it's a string, we can't find the full object without backend data
    return null;
  }, [value]);

  // Get string representation for display
  const displayValue = useMemo(() => {
    if (!value) return '';

    if (typeof value === 'object' && 'ticker' in value) {
      // Check if it has 'name' (TickerSearchResult) or 'companyName' (CustomHinShare)
      const name = 'name' in value ? value.name : value.companyName;
      return `${value.ticker} - ${name}`;
    }

    if (typeof value === 'string') {
      return value;
    }

    return '';
  }, [value]);

  // Read-only mode
  if (mode === 'read') {
    if (!displayValue) return null;

    return (
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-sm font-medium">
          Company
        </span>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-foreground text-sm">{displayValue}</span>
        </div>
      </div>
    );
  }

  // Edit mode
  const handleSelect = (stock: TickerSearchResult | null) => {
    if (stock) {
      onChange(stock);
    } else {
      onChange(null);
    }
    setOpen(false);
    setSearchTerm('');
  };

  const handleAddNew = () => {
    if (!newTicker.trim() || !newCompanyName.trim()) return;

    // Create custom HIN share object
    const customShare: CustomHinShare = {
      ticker: newTicker.trim().toUpperCase(),
      companyName: newCompanyName.trim(),
      isCustom: true
    };

    onChange(customShare);

    // Reset state
    setShowAddNew(false);
    setNewTicker('');
    setNewCompanyName('');
  };

  const handleCancelAddNew = () => {
    setShowAddNew(false);
    setNewTicker('');
    setNewCompanyName('');
  };

  /*
    `cell` is compact *styling* without compact's own row wrapper — so it must
    answer true here, or the field falls back to the form idiom and renders its
    own stacked `Label` inside a grid row that already has one.
  */
  const compact = variant === 'compact' || variant === 'cell';

  const body = (
    <div className={cn('flex min-w-0 flex-col gap-1', compact && 'flex-1')}>
      {/*
        In compact the label is the row's own column (the wrapper at the end of this
        component), so the field never renders a second one — a stacked `Label` above
        an attribute row is the mixed-idiom seam the variant exists to remove.
      */}
      {!compact && (
        <Label>
          Ticker / Company Name
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {/* Searchable dropdown */}
      <Popover
        open={open}
        /*
          Dismissing the popover also leaves the create step — the same reset the
          institution picker needs, for the same reason: a `showAddNew` that
          survives a closed popover is a state nothing on screen can get out of.
        */
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setShowAddNew(false);
            setNewTicker('');
            setNewCompanyName('');
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={isLoading || disabled}
            className={cn(
              'bg-input-bg h-auto w-full justify-between rounded-md border-0 px-3 py-1.5 text-sm font-normal',
              /*
                The row's own inset and type, so an unset ticker reads as an empty
                attribute row rather than a control parked in one.
                `bg-transparent` from the bare token clears the trigger's own grey
                fill.
              */
              compact &&
                `${ATTRIBUTE_EDITOR_BARE} ${ATTRIBUTE_INSET} ${ATTRIBUTE_VALUE_TEXT} ${ATTRIBUTE_LEADING}`,
              /*
                **The ring and fill belong to the OPEN state only** (VALE-997).

                They were unconditional in compact, so this control sat in a box
                at rest while every value beside it sat in bare text — the box
                the reporter read as a card dropped into the form. Open, the
                chrome is correct and stays: it is then genuinely an editor. The
                institution picker beside it takes the identical treatment.
              */
              compact && (open ? ATTRIBUTE_EDITOR_CHROME : ATTRIBUTE_HOVER)
            )}
          >
            {currentStock ? (
              <div className="flex items-center gap-2 truncate">
                <span
                  className={cn(compact ? ATTRIBUTE_VALUE_INK : 'font-medium')}
                >
                  {currentStock.ticker}
                </span>
                <span
                  className={cn(
                    'truncate',
                    compact ? ATTRIBUTE_MUTED_INK : 'text-muted-foreground'
                  )}
                >
                  {'name' in currentStock
                    ? currentStock.name
                    : currentStock.companyName}
                </span>
              </div>
            ) : displayValue ? (
              <span className={cn(compact && ATTRIBUTE_VALUE_INK)}>
                {displayValue}
              </span>
            ) : (
              <span
                className={cn(
                  compact ? ATTRIBUTE_MUTED_INK : 'text-muted-foreground'
                )}
              >
                {compact
                  ? attributePlaceholder('Ticker')
                  : 'Search by company name or ticker...'}
              </span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={`${ATTRIBUTE_POPOVER_W} p-0`} align="start">
          {showAddNew ? (
            <InlineCreateStep
              title="New ticker"
              fields={[
                {
                  label: 'Ticker symbol',
                  value: newTicker,
                  placeholder: 'e.g. BHP',
                  autoFocus: true,
                  onChange: setNewTicker
                },
                {
                  label: 'Company name',
                  value: newCompanyName,
                  placeholder: 'e.g. BHP Group Ltd',
                  onChange: setNewCompanyName
                }
              ]}
              hint="Both are required. The ticker is stored uppercase."
              submitLabel="Add ticker"
              onCancel={handleCancelAddNew}
              onSubmit={handleAddNew}
            />
          ) : (
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Enter ticker or company name..."
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
              <CommandList>
                {isLoading ? (
                  <div className="text-muted-foreground p-4 text-center text-sm">
                    Searching...
                  </div>
                ) : stocks.length === 0 && searchTerm.length >= 2 ? (
                  <CommandEmpty>No stocks found.</CommandEmpty>
                ) : (
                  <CommandGroup>
                    <CommandItem
                      value="none"
                      onSelect={() => handleSelect(null)}
                    >
                      <span className="text-muted-foreground">None</span>
                    </CommandItem>
                    {stocks.map((stock) => (
                      <CommandItem
                        key={`${stock.ticker}-${stock.exchange}`}
                        value={`${stock.ticker} ${stock.name}`}
                        onSelect={() => handleSelect(stock)}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0',
                              currentStock?.ticker === stock.ticker
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {stock.ticker}
                              </span>
                              <span className="text-muted-foreground text-sm">
                                {stock.name}
                              </span>
                            </div>
                            <span className="text-muted-foreground text-xs">
                              {stock.exchange} · {stock.currency}
                            </span>
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {/*
                "Add new ticker" is the last item in the list, not a helper row
                under the field (LF-6314) — the same change made to
                `InstitutionSearchField`, applied here because this is the same
                pattern rather than a similar-looking one. Left as a helper row it
                wrapped at the inventory drawer's width and asked the question
                where the user is not looking: you only conclude the ticker is
                missing after searching, by which point you are inside the
                popover.
              */}
                {!disabled && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        value="__add_new_ticker"
                        // Keeps the popover open and advances it to the create
                        // step — see `InlineCreateStep` for why.
                        onSelect={() => setShowAddNew(true)}
                        className="text-primary"
                      >
                        <Plus className="mr-2 h-4 w-4 shrink-0" />
                        Add a new ticker
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );

  /*
    **`cell` is the control with no row of its own** (VALE-863). `compact` wraps
    itself in a label column plus value, which is right when the field is its own
    block — but a field that sits *inside* the attribute grid is already in a row
    the grid drew, and wrapping again gave it a second label column and its own
    full-width line. That is what made a share drawer alternate between one field
    on a line and two, with the single ones stretched across the whole width for
    no reason.

    `body` is exactly the control, so the host row supplies the label and the
    value slot supplies the width.
  */
  if (variant === 'cell') return body;

  if (!compact) return body;

  /*
    **The compact field is a row, label column and all** — the same shape
    `InstitutionSearchField` takes, and for the same reason: an unlabelled full-bleed
    control is the one row in a panel with no left column, which is what an isolated
    review led with when the institution field had it.

    The label column has to be published here too. `ATTRIBUTE_LABEL` is
    `w-[var(--attr-label-w)]`, and an undefined custom property makes that width
    invalid — the label then falls back to its own text width and the row starts its
    value somewhere different from every other row. `columns={2}` selects the formula
    the surrounding grid resolves, so the two agree at every width.
  */
  return (
    /*
      **The variable, but NOT a new `@container`** — and that distinction is a bug
      this walked into before it was written down.

      `ATTRIBUTE_LABEL_WIDTH` is a container query, so it is resolved against the
      nearest `@container` ancestor. When this field sits inside a two-up pair (the HIN
      form puts the broker beside the HIN), declaring `@container` here made that
      ancestor *this half-width cell* — so its label column came out roughly half the
      width of every other row's, and it was the first row on the panel. Publishing only
      the variable leaves the query resolving against whatever block-level container the
      host established, which is the one every other row is measured by.

      The variable is still set here rather than left to the host because
      `ATTRIBUTE_LABEL`'s `w-[var(--attr-label-w)]` is *invalid* when the property is
      undefined — the label then silently collapses to its own text width, which is a
      failure with no visible cause.
    */
    /*
      `@container` added (VALE-863) for the same reason as the SRN twin: the
      published width has to resolve against a container the same size as the
      grid's own, or this row lands on a different left edge from every row
      beside it.
    */
    <div className="@container" style={attributeLabelWidthStyle(2)}>
      {/*
        `Ticker` rather than `Ticker / Company Name`: the label column is roughly
        120px and the long form ellipsised to `Ticker / Compan…`. The company name is
        not lost — the row's own value shows the ticker *and* the company beside it,
        which is where a reader looks for it anyway.
      */}
      <AttributeRow label="Ticker" required={required}>
        {body}
      </AttributeRow>
    </div>
  );
}
